/**
 * The Publish dialog's state machine (refreshable-report spec, design §6).
 *
 *   loading ─┬─ existing (already published: edit / unpublish, F1 unchanged)
 *            └─ question ──► running ──► review ──► details ──► published
 *                 │  "Publish snapshot instead"        ▲
 *                 └────────────────────────────────────┘ (mode = snapshot)
 *
 * PDF-based source turns cannot be re-run (Req 2.7) and go straight from
 * loading to details as a snapshot. A pure reducer so every transition is
 * unit-testable without rendering.
 */

import type { DraftedQuestion, PublishedReportSummary } from "../../models/API";
import type { RunOutcome } from "./reportRun";

export type PublishStep =
  | "loading"
  | "existing"
  | "question"
  | "running"
  | "review"
  | "details"
  | "published";

export interface RunHandle {
  runThread: string;
  runTurnId: string;
  /** Epoch ms when the run was started (for the elapsed-time display). */
  startedAt: number;
}

export interface PublishFlowState {
  step: PublishStep;
  mode: "snapshot" | "rerun";
  /** True while draftReportQuestion is in flight. */
  drafting: boolean;
  draft: DraftedQuestion | null;
  /** The editable defining question (seeded from the draft). */
  question: string;
  run: RunHandle | null;
  /** The finished run being reviewed (succeeded) or shown as failed. */
  outcome: RunOutcome | null;
  existing: PublishedReportSummary | null;
  published: PublishedReportSummary | null;
}

export type PublishFlowAction =
  | { type: "reset" }
  | { type: "precheck_found"; report: PublishedReportSummary }
  | { type: "precheck_none"; pdf: boolean }
  | { type: "draft_ready"; draft: DraftedQuestion }
  | { type: "draft_failed" }
  | { type: "edit_question"; question: string }
  | { type: "run_started"; run: RunHandle }
  | { type: "run_finished"; outcome: RunOutcome }
  | { type: "cancel_wait" }
  | { type: "use_snapshot" }
  | { type: "back_to_question" }
  | { type: "to_details" }
  | { type: "back_to_review" }
  | { type: "published"; report: PublishedReportSummary };

export const initialPublishFlowState: PublishFlowState = {
  step: "loading",
  mode: "rerun",
  drafting: false,
  draft: null,
  question: "",
  run: null,
  outcome: null,
  existing: null,
  published: null,
};

export function publishFlowReducer(
  state: PublishFlowState,
  action: PublishFlowAction
): PublishFlowState {
  switch (action.type) {
    case "reset":
      return initialPublishFlowState;
    case "precheck_found":
      return {
        ...initialPublishFlowState,
        step: "existing",
        existing: action.report,
      };
    case "precheck_none":
      // A PDF-based answer cannot be reproduced by a question alone (Req 2.7).
      return action.pdf
        ? { ...initialPublishFlowState, step: "details", mode: "snapshot" }
        : { ...initialPublishFlowState, step: "question", drafting: true };
    case "draft_ready":
      return {
        ...state,
        drafting: false,
        draft: action.draft,
        question: action.draft.question,
      };
    case "draft_failed":
      return { ...state, drafting: false, draft: null };
    case "edit_question":
      return { ...state, question: action.question };
    case "run_started":
      return { ...state, step: "running", run: action.run, outcome: null };
    case "run_finished":
      // A cancelled wait returns to the question; everything else is reviewed
      // (a failure shows its message with "Edit question" / snapshot options).
      if (action.outcome.status === "cancelled") {
        return { ...state, step: "question", run: null, outcome: null };
      }
      return { ...state, step: "review", outcome: action.outcome };
    case "cancel_wait":
      return { ...state, step: "question", run: null, outcome: null };
    case "use_snapshot":
      return {
        ...state,
        step: "details",
        mode: "snapshot",
        run: null,
        outcome: null,
      };
    case "back_to_question":
      return {
        ...state,
        step: "question",
        mode: "rerun",
        run: null,
        outcome: null,
      };
    case "to_details":
      return { ...state, step: "details", mode: "rerun" };
    case "back_to_review":
      return state.outcome ? { ...state, step: "review" } : state;
    case "published":
      return { ...state, step: "published", published: action.report };
    default:
      return state;
  }
}

/** Whether the review step holds a usable answer to publish. */
export function hasPublishableRun(state: PublishFlowState): boolean {
  return (
    state.step !== "loading" &&
    state.mode === "rerun" &&
    state.run !== null &&
    state.outcome?.status === "succeeded"
  );
}
